import { createClient } from '@supabase/supabase-js';
import { parseSupportMessageContent, sanitizeSupportMessageText, serializeSupportMessageContent } from './supportMessageContent';

export const SUPABASE_URL = 'https://fatssptzqfshlxwgojtv.supabase.co';
export const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImZhdHNzcHR6cWZzaGx4d2dvanR2Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzM4MzE3ODEsImV4cCI6MjA4OTQwNzc4MX0.lrARHtWPHXa6uPIO4NVMmnR1ckao-T1vxn5VxsG5X7A';

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

const callPublicAdminApi = async ({ action, body }) => {
  const response = await fetch(`${SUPABASE_URL}/functions/v1/admin-api`, {
    method: 'POST',
    headers: {
      'apikey': SUPABASE_ANON_KEY,
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ action, ...body }),
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.error || 'Request failed');
  }

  return data;
};

const getAccessToken = async () => {
  // getSession returns cached token; if expired, auto-refresh
  let { data, error } = await supabase.auth.getSession();
  if (error) throw error;
  if (!data.session) return null;
  // If token expires within the next 60 seconds, force refresh now
  const expiresAt = data.session.expires_at; // seconds since epoch
  if (expiresAt && expiresAt < Math.floor(Date.now() / 1000) + 60) {
    const refreshed = await supabase.auth.refreshSession();
    if (refreshed.error) throw refreshed.error;
    return refreshed.data.session?.access_token || null;
  }
  return data.session.access_token || null;
};

const callAdminApi = async ({ action, body }) => {
  const accessToken = await getAccessToken();
  if (!accessToken) {
    throw new Error('You must be signed in to perform admin actions');
  }

  const response = await fetch(`${SUPABASE_URL}/functions/v1/admin-api`, {
    method: 'POST',
    headers: {
      'apikey': SUPABASE_ANON_KEY,
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ action, ...body }),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    // Supabase gateway errors use 'message'; function errors use 'error'
    throw new Error(data?.error || data?.message || `Request failed (${response.status})`);
  }

  return data;
};

export const getAdminStatus = async (accessToken) => {
  if (accessToken) {
    const res = await fetch(`${SUPABASE_URL}/functions/v1/admin-api`, {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ action: 'is-admin' }),
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.error || data.message || 'Failed to determine admin status');
    }

    return data;
  }

  return callAdminApi({ action: 'is-admin' });
};

export const adminFetchAllRestaurants = async () => (
  callAdminApi({ action: 'restaurants' })
);

export const adminGetUser = async (userId) => {
  return callAdminApi({ action: 'user', body: { userId } });
};

export const adminUpdateUser = async (userId, updates) => {
  return callAdminApi({ action: 'update-user', body: { userId, updates } });
};

export const adminDeleteUser = async (userId) => {
  return callAdminApi({ action: 'delete-user', body: { userId } });
};

export const adminCloseStoreLifecycle = async (restaurantId, reason = '') => {
  return callAdminApi({
    action: 'admin-close-store',
    body: { restaurantId, reason },
  });
};

export const adminReactivateStoreLifecycle = async (restaurantId) => {
  return callAdminApi({
    action: 'admin-reactivate-store',
    body: { restaurantId },
  });
};

export const adminReassignStoreOwner = async (restaurantId, ownerEmail, invitedAt) => {
  return callAdminApi({
    action: 'admin-reassign-store-owner',
    body: { restaurantId, ownerEmail, invitedAt },
  });
};

export const adminGetStoreLifecycleHistory = async (restaurantId) => {
  return callAdminApi({
    action: 'admin-get-store-lifecycle-history',
    body: { restaurantId },
  });
};

export const adminInviteUser = async (email, redirectTo) => {
  return callAdminApi({ action: 'invite-user', body: { email, redirectTo } });
};

export const adminEnsureUserByEmail = async (email, redirectTo) => {
  return callAdminApi({ action: 'ensure-user-by-email', body: { email, redirectTo } });
};

export const acceptPendingStoreOwnerInvites = async () => {
  const { data, error } = await supabase.rpc('accept_pending_store_owner_invites');
  if (error) throw error;
  return data;
};

export const adminSetPendingStoreOwner = async (restaurantId, ownerEmail, invitedAt) => {
  const { data, error } = await supabase.rpc('admin_set_pending_store_owner', {
    p_restaurant_id: restaurantId,
    p_owner_email: ownerEmail,
    p_invited_at: invitedAt,
  });
  if (error) throw error;
  return data;
};

export const adminClearStoreOwner = async (restaurantId) => {
  const { data, error } = await supabase.rpc('admin_clear_store_owner', {
    p_restaurant_id: restaurantId,
  });
  if (error) throw error;
  return data;
};

export const checkSignupEmail = async (email) => {
  return callPublicAdminApi({ action: 'check-signup-email', body: { email } });
};

export const submitStoreAccessRequest = async (request) => {
  const { error } = await supabase
    .from('store_access_requests')
    .insert(request);

  if (error) throw error;
  return {};
};

export const submitSupportMessage = async (message) => {
  const { data, error } = await supabase
    .from('support_messages')
    .insert(message)
    .select('*')
    .single();

  if (error) throw error;
  return data;
};

export const notifyAdmin = async (notification) => {
  return callPublicAdminApi({ action: 'notify-admin', body: notification });
};

export const uploadSupportAttachment = async (file) => {
  if (!file) return null;
  if (file.size > 8 * 1024 * 1024) throw new Error('Attachments must be 8 MB or smaller.');

  const ext = file.name.includes('.') ? file.name.split('.').pop() : '';
  const objectPath = `${crypto.randomUUID()}${ext ? '.' + ext : ''}`;

  const { error } = await supabase.storage
    .from('support-message-assets')
    .upload(objectPath, file, { contentType: file.type || 'application/octet-stream', upsert: false });

  if (error) throw new Error(error.message || 'Upload failed');

  const { data: urlData } = supabase.storage.from('support-message-assets').getPublicUrl(objectPath);
  return { url: urlData.publicUrl, fileName: file.name, contentType: file.type, size: file.size };
};

export const uploadPublicSiteAsset = async (file, folder = 'general') => {
  if (!file) return null;
  if (file.size > 15 * 1024 * 1024) throw new Error('Images or media must be 15 MB or smaller.');

  const safeFolder = String(folder || 'general').replace(/[^a-z0-9-_]/gi, '-').toLowerCase();
  const buffer = await file.arrayBuffer();
  let binary = '';
  const bytes = new Uint8Array(buffer);
  for (let index = 0; index < bytes.length; index += 1) {
    binary += String.fromCharCode(bytes[index]);
  }

  return callAdminApi({
    action: 'upload-public-site-asset',
    body: {
      fileName: file.name,
      contentType: file.type || 'application/octet-stream',
      folder: safeFolder,
      base64Data: btoa(binary),
    },
  });
};

// Append a message to a thread — direct DB update, no edge function JWT path
export const appendThreadMessage = async (messageId, { role, text, attachments = [], name = '' }) => {
  // Read current thread
  const { data: msg, error: fetchErr } = await supabase
    .from('support_messages')
    .select('thread_messages')
    .eq('id', messageId)
    .single();
  if (fetchErr) throw fetchErr;

  const thread = Array.isArray(msg?.thread_messages) ? msg.thread_messages : [];
  const entry = { role, text, attachments, name, created_at: new Date().toISOString() };

  const { error: updateErr } = await supabase
    .from('support_messages')
    .update({ thread_messages: [...thread, entry] })
    .eq('id', messageId);
  if (updateErr) throw updateErr;

  return { success: true };
};

export const adminReplySupportMessage = async (messageId, replyPayload) => {
  const { reply, attachments = [] } = replyPayload;
  const { data: { user } } = await supabase.auth.getUser();
  const name = user?.user_metadata?.name || user?.user_metadata?.full_name || user?.email || 'Admin';

  // Read current thread + message details needed for email
  const { data: msg, error: fetchErr } = await supabase
    .from('support_messages')
    .select('thread_messages, from_email, subject, message, source')
    .eq('id', messageId)
    .single();
  if (fetchErr) throw fetchErr;

  const thread = Array.isArray(msg?.thread_messages) ? msg.thread_messages : [];
  const entry = { role: 'admin', text: reply, attachments, name, created_at: new Date().toISOString() };
  const storedReply = serializeSupportMessageContent(reply, attachments);

  // Update DB directly — admin UPDATE policy allows this
  const { error: updateErr } = await supabase
    .from('support_messages')
    .update({
      thread_messages: [...thread, entry],
      admin_reply: storedReply,
      admin_replied_at: entry.created_at,
      admin_replied_by_user_id: user?.id || null,
      status: 'in_progress',
    })
    .eq('id', messageId);
  if (updateErr) throw updateErr;

  // Send email via public action — pass all data directly, no DB read in edge function
  const { text: origText, attachments: origAttachments } = parseSupportMessageContent(msg?.message || '');
  const sanitizedOriginalMessage = msg?.source === 'account-deletion'
    ? sanitizeSupportMessageText(origText)
    : origText;
  try {
    const email = await callPublicAdminApi({
      action: 'send-reply-notification',
      body: {
        toEmail: msg?.from_email || '',
        replyText: reply,
        replyAttachments: attachments,
        originalSubject: msg?.subject || '',
        originalMessage: sanitizedOriginalMessage,
        originalAttachments: origAttachments || [],
      },
    });
    return { email };
  } catch (emailErr) {
    return { email: { delivered: false, error: emailErr.message } };
  }
};

export const adminDeleteSupportMessage = async (messageId) => {
  // Use direct Supabase call — the RLS policy already grants delete to the admin user.
  // This avoids the edge function JWT path which is unreliable for admin actions.
  const { error } = await supabase.from('support_messages').delete().eq('id', messageId);
  if (error) throw error;
  return { success: true };
};

export const notifyStoreRequestDecision = async ({ email, firstName, storeName, decision }) => {
  const session = await supabase.auth.getSession();
  const token = session?.data?.session?.access_token;
  await fetch(`${SUPABASE_URL}/functions/v1/admin-api`, {
    method: 'POST',
    headers: {
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ action: 'notify-store-request-decision', email, firstName, storeName, decision }),
  });
};

export const getMyBillingSubscription = async () => {
  return callAdminApi({ action: 'get-my-billing-subscription' });
};

export const createBillingCheckoutSession = async ({ interval, successUrl, cancelUrl }) => {
  return callAdminApi({
    action: 'create-billing-checkout-session',
    body: { interval, successUrl, cancelUrl },
  });
};

export const syncBillingFromCheckoutSession = async (sessionId) => {
  return callAdminApi({
    action: 'sync-billing-from-checkout-session',
    body: { sessionId },
  });
};

export const createBillingPortalSession = async (returnUrl) => {
  return callAdminApi({
    action: 'create-billing-portal-session',
    body: { returnUrl },
  });
};

export const adminApproveStoreAccessRequest = async (requestId) => {
  const { data, error } = await supabase.rpc('admin_approve_store_access_request', {
    p_request_id: requestId,
  });
  if (error) throw error;
  return data;
};

export const adminRejectStoreAccessRequest = async (requestId) => {
  const { data, error } = await supabase.rpc('admin_reject_store_access_request', {
    p_request_id: requestId,
  });
  if (error) throw error;
  return data;
};

export const fetchPublicSiteContent = async () => {
  const { data, error } = await supabase
    .from('public_site_content')
    .select('page_slug, content, updated_at, updated_by_user_id');

  if (error) throw error;
  return data || [];
};

export const upsertPublicSitePageContent = async (pageSlug, content) => {
  return callAdminApi({
    action: 'upsert-public-site-content',
    body: { pageSlug, content },
  });
};

export const deletePublicSitePageContent = async (pageSlug) => {
  return callAdminApi({
    action: 'delete-public-site-content',
    body: { pageSlug },
  });
};

export const mergeRestaurantStoreSettings = async (restaurantId, patch) => {
  const { data, error: fetchErr } = await supabase
    .from('restaurants')
    .select('store_settings')
    .eq('id', restaurantId)
    .single();
  if (fetchErr) throw fetchErr;
  const merged = { ...(data?.store_settings || {}), ...patch };
  const { error } = await supabase
    .from('restaurants')
    .update({ store_settings: merged })
    .eq('id', restaurantId);
  if (error) throw error;
};

export const fetchStorePages = async (tenantId) => {
  const { data, error } = await supabase
    .from('store_pages')
    .select('*')
    .eq('tenant_id', tenantId)
    .order('sort_order');
  if (error) throw error;
  return data || [];
};

export const upsertStorePage = async (tenantId, pageKey, fields) => {
  const { data, error } = await supabase
    .from('store_pages')
    .upsert({ tenant_id: tenantId, page_key: pageKey, ...fields, updated_at: new Date().toISOString() }, { onConflict: 'tenant_id,page_key' })
    .select('*')
    .single();
  if (error) throw error;
  return data;
};

export const setStoreLandingPage = async (tenantId, pageKey) => {
  // Clear all landing flags for this tenant then set the one
  await supabase.from('store_pages').update({ is_landing: false }).eq('tenant_id', tenantId);
  const { error } = await supabase.from('store_pages').update({ is_landing: true }).eq('tenant_id', tenantId).eq('page_key', pageKey);
  if (error) throw error;
};

// ── Store Website Builder ──────────────────────────────────────────────────

export const fetchStoreSiteContent = async (restaurantId) => {
  const { data, error } = await supabase
    .from('store_site_content')
    .select('page_slug, content, updated_at, updated_by_user_id')
    .eq('restaurant_id', restaurantId);
  if (error) throw error;
  return data || [];
};

export const upsertStoreSiteContent = async (restaurantId, pageSlug, content) => {
  const { data: { user } } = await supabase.auth.getUser();
  const { error } = await supabase
    .from('store_site_content')
    .upsert(
      { restaurant_id: restaurantId, page_slug: pageSlug, content, updated_at: new Date().toISOString(), updated_by_user_id: user?.id || null },
      { onConflict: 'restaurant_id,page_slug' }
    );
  if (error) throw error;
};

export const uploadStoreSiteAsset = async (restaurantId, file, folder = 'general') => {
  if (!file) return null;
  if (file.size > 15 * 1024 * 1024) throw new Error('Images must be 15 MB or smaller.');
  const safeFolder = String(folder || 'general').replace(/[^a-z0-9-_]/gi, '-').toLowerCase();
  const fileExt = (file.name || '').split('.').pop() || 'bin';
  const fileName = `${Date.now()}-${Math.random().toString(36).slice(2)}.${fileExt}`;
  const objectPath = `${restaurantId}/${safeFolder}/${fileName}`;
  const { error } = await supabase.storage
    .from('store-site-assets')
    .upload(objectPath, file, { contentType: file.type || 'application/octet-stream', upsert: false });
  if (error) throw new Error(error.message || 'Upload failed');
  const { data: urlData } = supabase.storage.from('store-site-assets').getPublicUrl(objectPath);
  return { url: urlData.publicUrl };
};
